import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Wrench, Play, Pause, Loader2, AlertTriangle, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";

/**
 * One-click maintenance + overnight enrichment toggle.
 *
 * Starting maintenance does THREE things at once:
 *   1) Flips the maintenance gate ON → public visitors see a "Be back soon" splash
 *   2) Pings the auto-enrich worker with cranked-up caps (batch=200, conc=10)
 *   3) Disables itself idempotently, so leaving + coming back to /admin
 *      doesn't double-trigger
 *
 * Ending maintenance does the inverse — splash goes away, worker drops
 * back to user-traffic-safe caps (batch=50, conc=3). The worker is NOT
 * stopped, so any leftover work continues gently in the background.
 */
export default function MaintenanceCard() {
  const [data, setData] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [eta, setEta] = useState("");
  const [savingSched, setSavingSched] = useState(false);

  const pad = (n) => String(n).padStart(2, "0");

  const refresh = useCallback(async () => {
    try {
      const [{ data: m }, { data: e }] = await Promise.all([
        api.get("/admin/maintenance"),
        api.get("/admin/auto-enrich"),
      ]);
      setData(m);
      setEnrich(e);
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const onToggle = async () => {
    if (!data) return;
    const turningOn = !data.enabled;
    if (turningOn && !window.confirm(
      "This will put up the maintenance splash for ALL visitors and run the AI enrichment at full speed.\n\nProceed?"
    )) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/maintenance/toggle", {
        enabled: turningOn,
        estimated_done_at: turningOn && eta ? eta : null,
        start_enrich: true,
      });
      setData(res);
      toast.success(turningOn ? "Maintenance started — visitors now see the splash" : "Maintenance ended");
      refresh();
    } catch {
      toast.error("Couldn't toggle maintenance");
    } finally {
      setBusy(false);
    }
  };

  const onSaveSchedule = async (next) => {
    setSavingSched(true);
    try {
      const { data: res } = await api.post("/admin/maintenance/toggle", {
        schedule_enabled: next.schedule_enabled,
        start_hour: next.start_hour,
        end_hour: next.end_hour,
      });
      setData(res);
      toast.success(next.schedule_enabled
        ? `Nightly schedule ON · ${pad(next.start_hour)}:00 → ${pad(next.end_hour)}:00 UK`
        : "Nightly schedule OFF");
    } catch {
      toast.error("Couldn't save schedule");
    } finally {
      setSavingSched(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="border hairline-strong p-5 bg-white">
        <div className="flex items-center gap-2 text-sm mono text-[#666]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading maintenance status…
        </div>
      </div>
    );
  }

  const isOn = !!data.enabled;
  const processedSession = enrich?.total_processed_session || 0;
  const remaining = (enrich?.remaining_descriptions || 0) + (enrich?.remaining_facilities || 0);

  return (
    <div
      className={`border-2 p-5 ${isOn ? "border-[#ff4500] bg-[#fff4ee]" : "border-[#1f1f1f] bg-white"}`}
      data-testid="admin-maintenance-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 border-2 ${isOn ? "border-[#ff4500] bg-white" : "border-[#1f1f1f] bg-[#fff4ee]"}`}>
            <Wrench className={`w-5 h-5 ${isOn ? "text-[#ff4500]" : "text-[#1f1f1f]"}`} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-xl leading-tight">
              {isOn ? "Maintenance ON — site is offline to visitors" : "Maintenance mode"}
            </h2>
            <p className="text-[12px] mono text-[#666] mt-0.5">
              {isOn
                ? "Worker is running at max speed (batch 200 × conc 10). Tap End to bring the site back."
                : "Start to put up the 'Be back soon' splash + run AI enrichment at full speed. Best to do overnight."}
            </p>
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-3 py-2 border-2 flex-shrink-0 transition-colors ${
            isOn
              ? "bg-white border-[#ff4500] text-[#ff4500] hover:bg-[#fff]"
              : "bg-[#ff4500] border-[#ff4500] text-white hover:bg-[#e63d00]"
          } ${busy ? "opacity-60" : ""}`}
          data-testid="maintenance-toggle"
        >
          {isOn ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {isOn ? "End maintenance" : "Start maintenance"}
        </button>
      </div>

      {isOn && (
        <div className="flex items-start gap-2 p-3 bg-white border border-[#ff4500] text-[12px] mono mb-3">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#ff4500]" />
          <div>
            <strong>Visitors are seeing the splash.</strong> Worker is processing {processedSession.toLocaleString()} sites
            so far · {remaining.toLocaleString()} remaining. ETA: {(enrich?.eta_hours || 0).toFixed(1)}h
          </div>
        </div>
      )}

      {!isOn && (
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] mono uppercase tracking-wider text-[#666] mb-1">
              Optional · "Estimated done at" message shown to visitors
            </label>
            <input
              type="text"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
              placeholder="e.g. 7am UK time"
              className="w-full border hairline px-3 py-2 text-sm"
              data-testid="maintenance-eta-input"
            />
          </div>
          <div className="text-[11px] mono text-[#666] leading-relaxed">
            <strong>What happens when you start:</strong><br />
            ✓ Public visitors see a friendly "Be back soon" splash<br />
            ✓ Admin dashboard stays accessible — you can flip it off anytime<br />
            ✓ AI enrichment worker switches to full-speed (200 × 10)<br />
            ✓ Auto-refreshes status every 10 seconds
          </div>
        </div>
      )}

      {/* Nightly auto-schedule */}
      <div className="mt-4 pt-4 border-t hairline">
        <div className="flex items-start gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#1f1f1f] mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Nightly auto-schedule</div>
            <div className="text-[11px] mono text-[#666] mt-0.5">
              Automatically start maintenance overnight (UK time). Site comes back online for visitors at the end hour.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={!!data.schedule_enabled}
              disabled={savingSched}
              onChange={(e) => onSaveSchedule({
                schedule_enabled: e.target.checked,
                start_hour: data.start_hour ?? 23,
                end_hour: data.end_hour ?? 7,
              })}
              className="w-4 h-4 accent-[#ff4500]"
              data-testid="maintenance-schedule-enabled"
            />
            <span className="text-[11px] mono uppercase tracking-wider">{data.schedule_enabled ? "On" : "Off"}</span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] mono uppercase tracking-wider text-[#666] mb-1">Start (UK)</label>
            <select
              value={data.start_hour ?? 23}
              disabled={savingSched}
              onChange={(e) => onSaveSchedule({
                schedule_enabled: !!data.schedule_enabled,
                start_hour: Number(e.target.value),
                end_hour: data.end_hour ?? 7,
              })}
              className="w-full border hairline px-2 py-2 text-sm bg-white"
              data-testid="maintenance-start-hour"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{pad(h)}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] mono uppercase tracking-wider text-[#666] mb-1">End (UK)</label>
            <select
              value={data.end_hour ?? 7}
              disabled={savingSched}
              onChange={(e) => onSaveSchedule({
                schedule_enabled: !!data.schedule_enabled,
                start_hour: data.start_hour ?? 23,
                end_hour: Number(e.target.value),
              })}
              className="w-full border hairline px-2 py-2 text-sm bg-white"
              data-testid="maintenance-end-hour"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{pad(h)}:00</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2 text-[10px] mono text-[#888]">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Now (UK): {data.uk_now || "—"}
          </span>
          {data.schedule_enabled && (
            <span className={data.currently_in_window ? "text-[#ff4500] font-bold" : ""}>
              {data.currently_in_window ? "Inside window" : "Outside window"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
